import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Tooltip,
  Dropdown,
  DropdownMenu,
  NavList,
  NavListSection,
  NavListItem,
} from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';

const ExportDropdown = ({ extractionResults, onExport }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const exportFile = (filename, data, type = 'application/json') => {
    console.log('exportFile called with:', {
      filename,
      dataLength: data.length,
      type,
    });
    try {
      const blob = new Blob([data], { type });
      console.log('Blob created:', blob);

      if (window.navigator.msSaveOrOpenBlob) {
        console.log('Using IE method');
        window.navigator.msSaveBlob(blob, filename);
      } else {
        console.log('Using standard method');
        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = filename;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
        console.log('File download triggered');
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportJSON = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('Export JSON clicked', extractionResults);
    if (!extractionResults) {
      console.log('No extraction results available');
      return;
    }
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `extraction-results-${timestamp}.json`;
    const jsonData = JSON.stringify(extractionResults, null, 2);
    console.log('JSON data prepared:', jsonData.substring(0, 100) + '...');
    exportFile(filename, jsonData);
    onExport?.('json');
    setTimeout(() => setDropdownOpen(false), 100);
  };

  const handleExportText = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('Export Text clicked', extractionResults);
    if (!extractionResults) {
      console.log('No extraction results available');
      return;
    }
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `extraction-results-${timestamp}.txt`;
    const { extractedData } = extractionResults;

    let content = `Extraction Results\n\n`;
    content += `Title: ${extractedData.entities?.title || 'Not specified'}\n`;
    content += `Author: ${Array.isArray(extractedData.entities?.authors) ? extractedData.entities.authors.join(', ') : extractedData.entities?.authors || 'Not specified'}\n`;
    content += `University: ${extractedData.entities?.university || 'Not specified'}\n`;
    content += `College: ${extractedData.entities?.college || 'Not specified'}\n`;
    content += `Department: ${extractedData.entities?.department || 'Not specified'}\n`;
    content += `Publication Year: ${extractedData.entities?.publication_year || 'Not specified'}\n`;
    content += `Language: ${Array.isArray(extractedData.entities?.language) ? extractedData.entities.language.join(', ') : extractedData.entities?.language || 'Not specified'}\n\n`;
    content += `Full Extracted Text:\n${extractedData.extractedText || 'No extracted text'}`;

    console.log('Text content prepared:', content.substring(0, 100) + '...');
    exportFile(filename, content, 'text/plain');
    onExport?.('text');
    setTimeout(() => setDropdownOpen(false), 100);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const renderTrigger = ({ getTriggerProps }) => (
    <Tooltip
      id="export-results-tooltip"
      text={
        <FormattedMessage
          id="ui-plugin-inventory-templates.imageExtraction.exportResults"
          defaultMessage="Export Results"
        />
      }
    >
      {({ ref, ariaIds }) => (
        <Button
          ref={ref}
          aria-labelledby={ariaIds.text}
          buttonStyle="slim"
          bottomMargin0
          {...getTriggerProps()}
        >
          <Icon icon="download" size="large" />
        </Button>
      )}
    </Tooltip>
  );

  const renderMenu = ({ open }) => (
    <DropdownMenu open={open}>
      <NavList>
        <NavListSection>
          <NavListItem
            id="export-json-item"
            type="button"
            onClick={handleExportJSON}
          >
            <Icon icon="export">
              <FormattedMessage
                id="ui-plugin-inventory-templates.imageExtraction.exportJSON"
                defaultMessage="Export as JSON"
              />
            </Icon>
          </NavListItem>
          <NavListItem
            id="export-text-item"
            type="button"
            onClick={handleExportText}
          >
            <Icon icon="document">
              <FormattedMessage
                id="ui-plugin-inventory-templates.imageExtraction.exportText"
                defaultMessage="Export as Text"
              />
            </Icon>
          </NavListItem>
        </NavListSection>
      </NavList>
    </DropdownMenu>
  );

  return (
    <Dropdown
      id="export-results-dropdown"
      renderTrigger={renderTrigger}
      renderMenu={renderMenu}
      open={dropdownOpen}
      onToggle={toggleDropdown}
      placement="bottom-end"
      focusHandlers={{ open: () => null }}
    />
  );
};

ExportDropdown.propTypes = {
  extractionResults: PropTypes.object,
  onExport: PropTypes.func,
};

export default ExportDropdown;
